// Parses the Excellence Program's per-week markdown body into a structure
// the panel can render as collapsible sections, instead of one long flat
// page. Every week's content already follows the same authored convention
// (see backend/scripts/populate-excellence-program.js and the 30 saved
// weeks): an "Intro to Week N" H2, then H2 sections whose titles start with
// "Theory" / "Examples" / anything else, with "Theory" broken into H3
// sub-topics and "Examples" broken into individually-headed example blocks
// (a bold "**Title** *(difficulty)*" line, a blockquote with the scenario/
// question/options, then a reveal — "**Correct answer:** ..." / "**Why:**
// ..." or the Most/Least Appropriate equivalent). This only changes how
// that same markdown is displayed; nothing about how admins author or save
// it changes.
//
// Deliberately tolerant of anything that doesn't match the convention
// (a week with no Theory/Examples at all, like the full-mock weeks, or
// legacy content authored before this convention existed) — those just
// fall back to a single generic collapsible section per H2, or one
// ungated block if there's no H2 structure at all.

const H2_REGEX = /^## (.+)$/gm;
const H3_REGEX = /^### (.+)$/gm;
// A whole line that's just "**Some Title** *(Some Meta)*" — the marker for
// where one example block ends and the next begins.
const EXAMPLE_HEADER_REGEX = /^\*\*(.+?)\*\*\s*\*\((.+?)\)\*\s*$/gm;

function splitByHeadingRegex(text, regex) {
  const matches = [...text.matchAll(regex)];
  const blocks = [];
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    blocks.push({ heading: matches[i][1].trim(), meta: matches[i][2] ?? null, content: text.slice(start, end).trim() });
  }
  return blocks;
}

// Within one example block, the question is the leading blockquote
// (contiguous "> " lines, tolerating a single blank line *inside* the quote
// if the next non-blank line is still quoted); everything from the first
// blank line that isn't followed by more quote is the answer/reveal.
function splitQuestionAnswer(block) {
  const lines = block.split('\n');
  let endIdx = 0;
  let sawQuoteLine = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('>')) {
      sawQuoteLine = true;
      endIdx = i + 1;
    } else if (sawQuoteLine && line.trim() === '') {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === '') j++;
      if (j < lines.length && lines[j].startsWith('>')) continue;
      break;
    } else if (sawQuoteLine) {
      break;
    }
  }
  return {
    question: lines.slice(0, endIdx).join('\n').trim(),
    answer: lines.slice(endIdx).join('\n').trim(),
  };
}

function parseExamplesSection(content) {
  const blocks = splitByHeadingRegex(content, EXAMPLE_HEADER_REGEX);
  if (blocks.length === 0) {
    return content ? [{ header: null, meta: null, question: content, answer: '' }] : [];
  }
  return blocks.map((b) => ({
    header: b.heading,
    meta: b.meta,
    ...splitQuestionAnswer(b.content),
  }));
}

function parseTheorySection(content) {
  const blocks = splitByHeadingRegex(content, H3_REGEX);
  if (blocks.length === 0) {
    return content ? [{ title: null, body: content }] : [];
  }
  return blocks.map((b) => ({ title: b.heading, body: b.content }));
}

export function parseWeekBody(body) {
  if (!body || !body.trim()) return { intro: '', sections: [] };

  const h2Blocks = splitByHeadingRegex(body, H2_REGEX);
  if (h2Blocks.length === 0) {
    // No recognised H2 structure — render the whole thing as one plain block.
    return { intro: '', sections: [{ title: null, kind: 'generic', body }] };
  }

  let intro = '';
  const sections = [];
  for (const block of h2Blocks) {
    if (/^intro to week/i.test(block.heading)) {
      intro = block.content;
    } else if (/^theory\b/i.test(block.heading)) {
      sections.push({ title: block.heading, kind: 'theory', subsections: parseTheorySection(block.content) });
    } else if (/^examples\b/i.test(block.heading)) {
      sections.push({ title: block.heading, kind: 'examples', examples: parseExamplesSection(block.content) });
    } else {
      sections.push({ title: block.heading, kind: 'generic', body: block.content });
    }
  }
  return { intro, sections };
}
